import React, { useMemo } from 'react';
import {
  Box,
  Alert,
} from '@mui/material';
import { useGetAllBalanceHistoryQuery } from '../../api/balanceHistoryApi';
import { useGetEmployeesQuery } from '../../api/employeesApi';
import BalanceHistoryTable from './components/BalanceHistoryTable';
import { AbsenceCategory, BalanceChangeType } from '../../constants/absenceEnums';
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from '../../constants/dataTable';
import { useDataTablePagination } from '../../hooks/useDataTablePagination';
import type { SearchableColumn } from '../../components/common/SearchInputs';
import type { AbsenceBalanceHistoryDto, BalanceHistoryFilters } from '../../types/balanceHistory';
import type { PaginatedQueryParams, PaginationParams } from '../../types/pagination';

const ISO_DATE_RANGE_PATTERN = /^(\d{4}-\d{2}-\d{2})-(\d{4}-\d{2}-\d{2})$/;

const parseDateRange = (value: string) => {
  const match = value.match(ISO_DATE_RANGE_PATTERN);
  if (!match) {
    return null;
  }

  return {
    fromDate: match[1],
    toDate: match[2],
  };
};

type BalanceHistoryQueryArg = {
  paginationParams?: PaginationParams;
} & Partial<BalanceHistoryFilters>;

const mapBalanceHistoryFilters = (
  filters: Record<string, any>
): Record<string, any> => {
  const nextFilters: Partial<BalanceHistoryFilters> = {};

  if (filters.createdAt) {
    const dateRange = parseDateRange(filters.createdAt);
    if (dateRange) {
      nextFilters.fromDate = dateRange.fromDate;
      nextFilters.toDate = dateRange.toDate;
    }
  }

  if (filters.employeeName !== null && filters.employeeName !== undefined) {
    nextFilters.employeeId = Number(filters.employeeName);
  }

  if (filters.absenceCategory !== null && filters.absenceCategory !== undefined) {
    nextFilters.category = Number(filters.absenceCategory) as AbsenceCategory;
  }

  if (filters.changeType !== null && filters.changeType !== undefined) {
    nextFilters.changeType = Number(filters.changeType) as BalanceChangeType;
  }

  return nextFilters;
};

const buildBalanceHistoryQueryArg = (
  queryParams: PaginatedQueryParams
): BalanceHistoryQueryArg => ({
  paginationParams: {
    pageNumber: queryParams.pageNumber,
    pageSize: queryParams.pageSize,
  },
  ...queryParams.filters,
});

const AdminBalanceHistoryPage: React.FC = () => {
  const {
    items: history,
    isLoading,
    isLoadingRows,
    isLoadingMore,
    totalCount,
    hasNextPage,
    error,
    handleSearchChange,
    handleLoadMore,
  } = useDataTablePagination<AbsenceBalanceHistoryDto, BalanceHistoryQueryArg>(
    useGetAllBalanceHistoryQuery,
    DEFAULT_DATA_TABLE_PAGE_SIZE,
    {
      buildQueryArg: buildBalanceHistoryQueryArg,
      mapSearchFilters: mapBalanceHistoryFilters,
    }
  );

  const { data: employees = [] } = useGetEmployeesQuery();

  const searchableColumns = useMemo<SearchableColumn[]>(() => [
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      searchable: true,
      searchType: 'date',
    },
    {
      field: 'employeeName',
      headerName: 'Employee',
      searchable: true,
      searchType: 'select',
      selectOptions: employees.map((employee) => ({
        value: employee.id,
        label: `${employee.firstName} ${employee.lastName}`,
      })),
    },
    {
      field: 'absenceCategory',
      headerName: 'Category',
      searchable: true,
      searchType: 'select',
      selectOptions: [
        { value: AbsenceCategory.Vacation, label: 'Vacation' },
        { value: AbsenceCategory.SickDays, label: 'Sick Days' },
      ],
    },
    {
      field: 'changeType',
      headerName: 'Change Type',
      searchable: true,
      searchType: 'select',
      selectOptions: [
        { value: BalanceChangeType.Earned, label: 'Earned' },
        { value: BalanceChangeType.Used, label: 'Used' },
        { value: BalanceChangeType.AdminAdjustment, label: 'Admin Adjustment' },
        { value: BalanceChangeType.CarryOver, label: 'Carry Over' },
        { value: BalanceChangeType.Expired, label: 'Expired' },
      ],
    },
  ], [employees]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load balance history. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
      <Box sx={{ p: 2 }}>
        <BalanceHistoryTable
          history={history}
          showEmployeeColumn={true}
          isLoading={isLoading}
          totalCount={totalCount || 0}
          hasNextPage={hasNextPage}
          isLoadingMore={isLoadingMore}
          isLoadingRows={isLoadingRows}
          onLoadMore={handleLoadMore}
          enableSearch
          searchableColumns={searchableColumns}
          onSearchChange={handleSearchChange}
        />
      </Box>
  );
};

export default AdminBalanceHistoryPage;


